import * as React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
// import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
// import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useAuth } from '../auth/AuthContext';
import { APP_PATHS } from '../auth/role-based-access-control';

type NavItem = {
  path: string;
  label: string;
  icon: React.ReactNode;
  exact?: boolean;
};

const dashboardNavItem: NavItem = {
  path: APP_PATHS.DASHBOARD,
  label: 'Dashboard',
  icon: <HomeRoundedIcon />,
  exact: true,
};

const bottomNavItems: NavItem[] = [
  { path: APP_PATHS.RECONCILIATION, label: 'Reconciliation', icon: <AccountBalanceWalletRoundedIcon />, exact: true },
  // All Audit Logs temporarily disabled
  // { path: APP_PATHS.AUDIT_LOGS, label: 'All Audit Logs', icon: <HistoryRoundedIcon />, exact: true },
];

const parentButtonSx = (active: boolean, collapsed: boolean) => ({
  borderRadius: '9px',
  px: collapsed ? 0 : '12px',
  py: '8px',
  minHeight: 36,
  justifyContent: collapsed ? 'center' : 'flex-start',
  gap: collapsed ? 0 : 1.5,
  transition: 'background-color 0.12s',
  color: active ? '#FFFFFF' : 'rgba(255,255,255,0.72)',
  ...(active
    ? {
        bgcolor: '#3636F2',
        '& .MuiListItemIcon-root': { color: '#FFFFFF' },
        '&:hover': { bgcolor: '#3636F2' },
        '&.Mui-selected': { bgcolor: '#3636F2' },
        '&.Mui-selected:hover': { bgcolor: '#3636F2' },
      }
    : {
        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
        '&.Mui-selected': { bgcolor: 'transparent' },
      }),
});

const iconSx = {
  minWidth: 0,
  color: 'inherit',
  justifyContent: 'center',
  flexShrink: 0,
  '& .MuiSvgIcon-root': { fontSize: 18, strokeWidth: 1.6 },
};

function SectionLabel({ children, collapsed }: { children: React.ReactNode; collapsed: boolean }) {
  if (collapsed) return null;
  return (
    <Typography
      sx={{
        px: '12px',
        pt: 1.5,
        pb: 0.5,
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
  );
}

interface MenuContentProps {
  onNavigate?: () => void;
  collapsed?: boolean;
}

interface SimpleItemProps {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  onClick: (path: string) => void;
}

function SimpleItem({ item, collapsed, active, onClick }: SimpleItemProps) {
  return (
    <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
      <ListItem disablePadding sx={{ mb: 0.25 }}>
        <ListItemButton
          onClick={() => onClick(item.path)}
          sx={parentButtonSx(active, collapsed)}
        >
          <ListItemIcon sx={iconSx}>{item.icon}</ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={item.label}
              slotProps={{
                primary: {
                  fontSize: '0.875rem',
                  fontWeight: active ? 600 : 500,
                  lineHeight: 1.4,
                  noWrap: true,
                  color: 'inherit',
                },
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    </Tooltip>
  );
}

interface SubItemProps {
  label: string;
  path: string;
  active: boolean;
  onClick: (path: string) => void;
}

function SubItem({ label, path, active, onClick }: SubItemProps) {
  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={active}
        onClick={() => onClick(path)}
        sx={{
          borderRadius: 1,
          py: 0.625,
          px: 1.25,
          minHeight: 34,
          transition: 'background-color 0.15s',
          '&.Mui-selected': { bgcolor: 'transparent' },
          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
        }}
      >
        <ListItemText
          primary={label}
          slotProps={{
            primary: {
              fontSize: '0.8125rem',
              fontWeight: active ? 600 : 400,
              color: active ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
              lineHeight: 1.4,
              noWrap: true,
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

interface ExpandableItemProps {
  icon: React.ReactNode;
  label: string;
  isOnRoute: boolean;
  isOpen: boolean;
  onToggle: () => void;
  collapsed: boolean;
  children: React.ReactNode;
}

function ExpandableItem({
  icon,
  label,
  isOnRoute,
  isOpen,
  onToggle,
  collapsed,
  children,
}: ExpandableItemProps) {
  return (
    <>
      <Tooltip title={collapsed ? label : ''} placement="right" arrow>
        <ListItem disablePadding sx={{ mb: 0.25 }}>
          <ListItemButton
            onClick={onToggle}
            sx={parentButtonSx(isOnRoute, collapsed)}
          >
            <ListItemIcon sx={iconSx}>{icon}</ListItemIcon>
            {!collapsed && (
              <>
                <ListItemText
                  primary={label}
                  slotProps={{
                    primary: {
                      fontSize: '0.875rem',
                      fontWeight: isOnRoute ? 600 : 500,
                      lineHeight: 1.4,
                      noWrap: true,
                      color: 'inherit',
                    },
                  }}
                />
                <Box
                  sx={{
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.65,
                    flexShrink: 0,
                  }}
                >
                  {isOpen ? (
                    <ExpandLessRoundedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
                  )}
                </Box>
              </>
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>

      {!collapsed && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              ml: 2.625,
              pl: 1.25,
              borderLeft: '1.5px solid',
              borderColor: 'rgba(255,255,255,0.12)',
              mb: 0.75,
            }}
          >
            <List dense disablePadding>
              {children}
            </List>
          </Box>
        </Collapse>
      )}
    </>
  );
}

export default function MenuContent({ onNavigate, collapsed = false }: MenuContentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  useAuth(); // Keep AuthContext mounted for any nested components that may rely on it.

  const isActive = (item: NavItem) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  const handleClick = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  /* ── Transactions (temporarily disabled) ── */
  // const onTransactionsRoute = location.pathname.startsWith(APP_PATHS.TRANSACTIONS);
  // const [transactionsOpen, setTransactionsOpen] = React.useState(onTransactionsRoute);
  // React.useEffect(() => {
  //   if (onTransactionsRoute) setTransactionsOpen(true);
  // }, [onTransactionsRoute]);

  /* ── Bulk SMS ── */
  const onBulkSmsRoute = location.pathname.startsWith(APP_PATHS.BULK_SMS);
  const [bulkSmsOpen, setBulkSmsOpen] = React.useState(onBulkSmsRoute);
  React.useEffect(() => {
    if (onBulkSmsRoute) setBulkSmsOpen(true);
  }, [onBulkSmsRoute]);

  /* ── Client Lookup ── */
  const onClientLookupRoute = location.pathname.startsWith(APP_PATHS.CLIENT_LOOKUP);
  const [clientLookupOpen, setClientLookupOpen] = React.useState(onClientLookupRoute);
  React.useEffect(() => {
    if (onClientLookupRoute) setClientLookupOpen(true);
  }, [onClientLookupRoute]);

  /* ─────────────────────────────────────────────────────── render ── */

  return (
    <Stack sx={{ flexGrow: 1, px: 1.5, py: 2 }}>
      <SectionLabel collapsed={collapsed}>Main</SectionLabel>
      <List dense disablePadding>
        {/* 1. Dashboard */}
        <SimpleItem
          item={dashboardNavItem}
          collapsed={collapsed}
          active={isActive(dashboardNavItem)}
          onClick={handleClick}
        />

        {/* 2. Client Lookup */}
        <ExpandableItem
          icon={<SearchRoundedIcon />}
          label="Client Lookup"
          isOnRoute={onClientLookupRoute}
          isOpen={clientLookupOpen}
          collapsed={collapsed}
          onToggle={() => {
            if (collapsed) {
              handleClick(APP_PATHS.CLIENT_LOOKUP);
            } else {
              setClientLookupOpen((v) => !v);
            }
          }}
        >
          <SubItem
            label="Lookup"
            path={APP_PATHS.CLIENT_LOOKUP}
            active={location.pathname === APP_PATHS.CLIENT_LOOKUP}
            onClick={handleClick}
          />
          <SubItem
            label="Contact updates logs"
            path={APP_PATHS.CLIENT_CHANGES_LOG}
            active={location.pathname === APP_PATHS.CLIENT_CHANGES_LOG}
            onClick={handleClick}
          />
        </ExpandableItem>

        {/* 3. Transactions (temporarily disabled)
        <ExpandableItem
          icon={<ReceiptLongRoundedIcon />}
          label="Transactions"
          isOnRoute={onTransactionsRoute}
          isOpen={transactionsOpen}
          collapsed={collapsed}
          onToggle={() => {
            if (collapsed) {
              handleClick(APP_PATHS.TRANSACTIONS_DEPOSITS);
            } else {
              setTransactionsOpen((v) => !v);
            }
          }}
        >
          <SubItem
            label="Deposits"
            path={APP_PATHS.TRANSACTIONS_DEPOSITS}
            active={location.pathname === APP_PATHS.TRANSACTIONS_DEPOSITS}
            onClick={handleClick}
          />
          <SubItem
            label="Withdrawals"
            path={APP_PATHS.TRANSACTIONS_WITHDRAWALS}
            active={location.pathname === APP_PATHS.TRANSACTIONS_WITHDRAWALS}
            onClick={handleClick}
          />
          <SubItem
            label="Transaction Logs"
            path={APP_PATHS.TRANSACTIONS_AUDITS}
            active={location.pathname === APP_PATHS.TRANSACTIONS_AUDITS}
            onClick={handleClick}
          />
        </ExpandableItem>
        */}
      </List>

      <SectionLabel collapsed={collapsed}>Marketing</SectionLabel>
      <List dense disablePadding>
        <ExpandableItem
          icon={<CampaignRoundedIcon />}
          label="Bulk SMS"
          isOnRoute={onBulkSmsRoute}
          isOpen={bulkSmsOpen}
          collapsed={collapsed}
          onToggle={() => {
            if (collapsed) {
              handleClick(APP_PATHS.BULK_SMS);
            } else {
              setBulkSmsOpen((v) => !v);
            }
          }}
        >
          <SubItem
            label="Send broadcast"
            path={APP_PATHS.BULK_SMS}
            active={location.pathname === APP_PATHS.BULK_SMS}
            onClick={handleClick}
          />
          <SubItem
            label="Delivery status"
            path={APP_PATHS.BULK_SMS_HISTORY}
            active={location.pathname === APP_PATHS.BULK_SMS_HISTORY}
            onClick={handleClick}
          />
        </ExpandableItem>
      </List>

      <SectionLabel collapsed={collapsed}>Finance</SectionLabel>
      <List dense disablePadding>
        <SimpleItem
          item={bottomNavItems[0]}
          collapsed={collapsed}
          active={isActive(bottomNavItems[0])}
          onClick={handleClick}
        />
      </List>

      {/* All Audit Logs temporarily disabled
      <SectionLabel collapsed={collapsed}>Audit</SectionLabel>
      <List dense disablePadding>
        <SimpleItem
          item={bottomNavItems[1]}
          collapsed={collapsed}
          active={isActive(bottomNavItems[1])}
          onClick={handleClick}
        />
      </List>
      */}
    </Stack>
  );
}
